import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Bell, Plus, Pencil, Trash2, Filter, Globe, Building2, 
  Search, Clock, Mail, Smartphone, X, AlertCircle,
  CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { usePlanGate, planGateFromResponse, planDialogModel } from '../lib/planGate';
import PlanLimitDialog from '../components/dialogs/PlanLimitDialog';
import { OnboardingEmptyState } from '../onboarding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// GCC Countries
const GCC_COUNTRIES = [
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'AE', name: 'UAE' },
  { code: 'KW', name: 'Kuwait' },
  { code: 'OM', name: 'Oman' },
  { code: 'BH', name: 'Bahrain' },
  { code: 'QA', name: 'Qatar' }
];

// Industry options
const INDUSTRIES = [
  'Construction', 'Infrastructure', 'Technology', 'Healthcare', 
  'Energy', 'Transportation', 'Defense', 'Education', 'Finance',
  'Real Estate', 'Manufacturing', 'Telecommunications', 'Tourism'
];

export default function AlertsPage() {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [savingAlert, setSavingAlert] = useState(false);
  const [planError, setPlanError] = useState(null);
  const [planGateInfo, setPlanGateInfo] = useState(null);
  const planGate = usePlanGate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    frequency: 'realtime',
    channels: ['in_app'],
    filters: {
      countries: [],
      industries: [],
      keywords: [],
      min_value: '',
      max_value: '',
      sources: []
    },
    is_active: true
  });
  
  const [keywordInput, setKeywordInput] = useState('');
  const [alertToDelete, setAlertToDelete] = useState(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        // Plan refusals are HTTP 402 with a plan_gate block (previously 403).
        // Route through the shared helper instead of comparing status codes.
        const pg = await planGateFromResponse(response);
        if (pg) {
          setPlanGateInfo(pg.gate);
          setPlanError(pg.message);
          setLoading(false);
          return;
        }
        throw new Error('Failed to fetch alerts');
      }

      const data = await response.json();
      setAlerts(data.alerts);
      setPlanError(null);
      setPlanGateInfo(null);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const resetForm = () => {
    setFormData({
      name: '',
      frequency: 'realtime',
      channels: ['in_app'],
      filters: {
        countries: [],
        industries: [],
        keywords: [],
        min_value: '',
        max_value: '',
        sources: []
      },
      is_active: true
    });
    setKeywordInput('');
    setEditingAlert(null);
  };

  const openEditDialog = (alert) => {
    setEditingAlert(alert);
    setFormData({
      name: alert.name,
      frequency: alert.frequency,
      channels: alert.channels || ['in_app'],
      filters: {
        countries: alert.filters?.countries || [],
        industries: alert.filters?.industries || [],
        keywords: alert.filters?.keywords || [],
        min_value: alert.filters?.min_value || '',
        max_value: alert.filters?.max_value || '',
        sources: alert.filters?.sources || []
      },
      is_active: alert.is_active
    });
    setShowCreateDialog(true);
  };

  const handleSaveAlert = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter an alert name');
      return;
    }
    
    setSavingAlert(true);
    try {
      const payload = {
        name: formData.name,
        frequency: formData.frequency,
        channels: formData.channels,
        filters: {
          countries: formData.filters.countries,
          industries: formData.filters.industries,
          keywords: formData.filters.keywords,
          min_value: formData.filters.min_value ? parseFloat(formData.filters.min_value) : null,
          max_value: formData.filters.max_value ? parseFloat(formData.filters.max_value) : null,
          sources: formData.filters.sources
        },
        is_active: formData.is_active
      };
      
      const url = editingAlert 
        ? `${API_URL}/api/alerts/${editingAlert._id}`
        : `${API_URL}/api/alerts`;
      
      const response = await fetch(url, {
        method: editingAlert ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        // Plan refusal → shared upgrade dialog (402 plan_gate; 403 legacy).
        const pg = await planGateFromResponse(response);
        if (pg) {
          setShowCreateDialog(false);
          planGate.show(pg);
          return;
        }
        // WO-33 — show the server's reason (e.g. "Choose at least one market…").
        const errData = await response.json().catch(() => ({}));
        throw new Error(typeof errData.detail === 'string' ? errData.detail : 'Failed to save alert');
      }
      
      toast.success(editingAlert ? 'Alert updated successfully' : 'Alert created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchAlerts();
    } catch (error) {
      console.error('Error saving alert:', error);
      toast.error(error.message || 'Failed to save alert');
    } finally {
      setSavingAlert(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/alerts/${alertId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to delete alert');
      
      toast.success('Alert deleted successfully');
      fetchAlerts();
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast.error('Failed to delete alert');
    }
  };

  const handleToggleActive = async (alert) => {
    try {
      const response = await fetch(`${API_URL}/api/alerts/${alert._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !alert.is_active })
      });
      
      if (!response.ok) {
        // WO-33 — e.g. an alert with no criteria cannot be switched back on.
        const errData = await response.json().catch(() => ({}));
        throw new Error(typeof errData.detail === 'string' ? errData.detail : 'Failed to update alert');
      }
      
      toast.success(`Alert ${!alert.is_active ? 'activated' : 'paused'}`);
      fetchAlerts();
    } catch (error) {
      console.error('Error toggling alert:', error);
      toast.error(error.message || 'Failed to update alert');
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.filters.keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        filters: {
          ...prev.filters,
          keywords: [...prev.filters.keywords, keywordInput.trim()]
        }
      }));
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        keywords: prev.filters.keywords.filter(k => k !== keyword)
      }
    }));
  };

  const toggleCountry = (code) => {
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        countries: prev.filters.countries.includes(code)
          ? prev.filters.countries.filter(c => c !== code)
          : [...prev.filters.countries, code]
      }
    }));
  };

  const toggleIndustry = (industry) => {
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        industries: prev.filters.industries.includes(industry)
          ? prev.filters.industries.filter(i => i !== industry)
          : [...prev.filters.industries, industry]
      }
    }));
  };

  const toggleChannel = (channel) => {
    setFormData(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };

  // Show plan upgrade message for free users
  if (planError) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        {/* WO-33 — helpful empty screen for Free (onboarding switch on); the old card otherwise. */}
        <OnboardingEmptyState kind="alertsFree" src="empty-alerts">
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Alerts Feature</h2>
          <p className="text-muted-foreground mb-6">{planError}</p>
          <Button onClick={() => { window.location.href = planDialogModel(planGateInfo).upgradeUrl; }}>
            Upgrade Your Plan
          </Button>
        </div>
        </OnboardingEmptyState>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="alerts-page">
      <PlanLimitDialog open={planGate.open} onOpenChange={planGate.setOpen} gate={planGate.gate} message={planGate.message} />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="w-6 h-6 text-primary" />
            Alerts
          </h1>
          <p className="text-muted-foreground mt-1">
            Get notified when new tenders, projects, or companies match your criteria
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateDialog(true); }} 
          data-testid="create-alert-btn"
          disabled={!!planError}
          className={planError ? 'opacity-50 cursor-not-allowed' : ''}
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Alert
        </Button>
      </div>

      {/* Alerts List */}
      {alerts.length === 0 ? (
        <OnboardingEmptyState kind="alerts" onPrimary={() => { resetForm(); setShowCreateDialog(true); }}>
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No alerts yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first alert to get notified about matching tenders, projects, or companies
            </p>
            <Button onClick={() => { resetForm(); setShowCreateDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Alert
            </Button>
          </CardContent>
        </Card>
        </OnboardingEmptyState>
      ) : (
        <div className="grid gap-4">
          {alerts.map((alert) => (
            <Card key={alert._id} className={`${!alert.is_active ? 'opacity-60' : ''}`} data-testid={`alert-card-${alert._id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold text-lg">{alert.name}</h3>
                      <Badge variant={alert.is_active ? 'default' : 'secondary'}>
                        {alert.is_active ? 'Active' : 'Paused'}
                      </Badge>
                      <Badge variant="outline" className="capitalize">
                        {alert.frequency === 'realtime' ? 'Real-time' : 'Daily Digest'}
                      </Badge>
                      {alert.is_own === false && (
                        <Badge variant="outline" className="text-xs bg-muted/60" data-testid={`alert-owner-${alert._id}`}>
                          {(language === 'ar' ? 'أنشأه ' : 'Created by ') + (alert.owner_name || (language === 'ar' ? 'زميل' : 'a teammate'))}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Filters Summary */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {alert.filters?.countries?.length > 0 && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Globe className="w-3.5 h-3.5" />
                          {alert.filters.countries.map(c => 
                            GCC_COUNTRIES.find(gc => gc.code === c)?.name || c
                          ).join(', ')}
                        </div>
                      )}
                      {alert.filters?.industries?.length > 0 && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Building2 className="w-3.5 h-3.5" />
                          {alert.filters.industries.slice(0, 3).join(', ')}
                          {alert.filters.industries.length > 3 && ` +${alert.filters.industries.length - 3}`}
                        </div>
                      )}
                      {alert.filters?.keywords?.length > 0 && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Search className="w-3.5 h-3.5" />
                          {alert.filters.keywords.slice(0, 3).join(', ')}
                          {alert.filters.keywords.length > 3 && ` +${alert.filters.keywords.length - 3}`}
                        </div>
                      )}
                    </div>
                    
                    {/* Notification Channels */}
                    <div className="mt-3 flex items-center gap-2">
                      {alert.channels?.includes('in_app') && (
                        <Badge variant="outline" className="text-xs">
                          <Bell className="w-3 h-3 mr-1" /> In-App
                        </Badge>
                      )}
                      {alert.channels?.includes('email') && (
                        <Badge variant="outline" className="text-xs">
                          <Mail className="w-3 h-3 mr-1" /> Email
                        </Badge>
                      )}
                      {alert.channels?.includes('push') && (
                        <Badge variant="outline" className="text-xs">
                          <Smartphone className="w-3 h-3 mr-1" /> Push
                        </Badge>
                      )}
                    </div>
                    
                    {/* Stats */}
                    <div className="mt-3 text-xs text-muted-foreground">
                      {alert.match_count > 0 && (
                        <span className="mr-4">
                          <CheckCircle className="w-3 h-3 inline mr-1" />
                          {alert.match_count} matches
                        </span>
                      )}
                      {alert.last_triggered_at && (
                        <span>
                          <Clock className="w-3 h-3 inline mr-1" />
                          Last triggered: {new Date(alert.last_triggered_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions — edit stays owner-only (server refuses others);
                      delete follows can_delete (creator / team admin / tenant admin). */}
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={alert.is_active}
                      onCheckedChange={() => handleToggleActive(alert)}
                      disabled={alert.is_own === false}
                      data-testid={`alert-toggle-${alert._id}`}
                    />
                    {alert.is_own !== false && (
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(alert)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {alert.can_delete && (
                      <Button variant="ghost" size="icon" onClick={() => setAlertToDelete(alert._id)} data-testid={`delete-alert-${alert._id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Alert Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateDialog(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAlert ? 'Edit Alert' : 'Create New Alert'}</DialogTitle>
            <DialogDescription>
              Set up filters to get notified when matching tenders, projects, or companies are published
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Alert Name */}
            <div className="space-y-2">
              <Label>Alert Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Saudi Construction Tenders"
                data-testid="alert-name-input"
              />
            </div>
            
            {/* Countries Filter */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Countries
              </Label>
              <div className="flex flex-wrap gap-2">
                {GCC_COUNTRIES.map((country) => (
                  <Badge
                    key={country.code}
                    variant={formData.filters.countries.includes(country.code) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleCountry(country.code)}
                    data-testid={`country-${country.code}`}
                  >
                    {formData.filters.countries.includes(country.code) && <X className="w-3 h-3 mr-1" />}
                    {country.name}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Industries Filter */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Industries
              </Label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg">
                {INDUSTRIES.map((industry) => (
                  <Badge
                    key={industry}
                    variant={formData.filters.industries.includes(industry) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleIndustry(industry)}
                  >
                    {formData.filters.industries.includes(industry) && <X className="w-3 h-3 mr-1" />}
                    {industry}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Keywords Filter */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Search className="w-4 h-4" />
                Keywords
              </Label>
              <div className="flex gap-2">
                <Input
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  placeholder="Enter keyword..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                />
                <Button type="button" variant="outline" onClick={addKeyword}>Add</Button>
              </div>
              {formData.filters.keywords.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.filters.keywords.map((keyword) => (
                    <Badge key={keyword} variant="secondary" className="cursor-pointer" onClick={() => removeKeyword(keyword)}>
                      {keyword}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            {/* WO-07 — Value Range control removed: budget filtering does not
                apply to tender alerts. min_value/max_value stay in state and are
                still sent as null so the request body shape is unchanged. */}
            
            {/* Frequency */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Notification Frequency
              </Label>
              <Select
                value={formData.frequency}
                onValueChange={(value) => setFormData(prev => ({ ...prev, frequency: value }))}
              >
                <SelectTrigger data-testid="frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="realtime">Real-time (Immediate)</SelectItem>
                  <SelectItem value="daily">Daily Digest</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Notification Channels */}
            <div className="space-y-2">
              <Label>Notification Channels</Label>
              <div className="flex flex-wrap gap-3">
                <div 
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                    formData.channels.includes('in_app') ? 'bg-primary/10 border-primary' : 'border-border'
                  }`}
                  onClick={() => toggleChannel('in_app')}
                >
                  <Bell className="w-4 h-4" />
                  <span>In-App</span>
                </div>
                <div 
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                    formData.channels.includes('email') ? 'bg-primary/10 border-primary' : 'border-border'
                  }`}
                  onClick={() => toggleChannel('email')}
                >
                  <Mail className="w-4 h-4" />
                  <span>Email</span>
                </div>
                <div 
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                    formData.channels.includes('push') ? 'bg-primary/10 border-primary' : 'border-border'
                  }`}
                  onClick={() => toggleChannel('push')}
                >
                  <Smartphone className="w-4 h-4" />
                  <span>Browser Push</span>
                </div>
              </div>
            </div>
            
            {/* Active Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Alert Status</Label>
                <p className="text-sm text-muted-foreground">Enable or disable this alert</p>
              </div>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowCreateDialog(false); }}>
              Cancel
            </Button>
            <Button onClick={handleSaveAlert} disabled={savingAlert} data-testid="save-alert-btn">
              {savingAlert ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingAlert ? 'Update Alert' : 'Create Alert'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!alertToDelete} onOpenChange={(open) => !open && setAlertToDelete(null)}>
        <AlertDialogContent data-testid="delete-alert-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete alert</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this alert? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-alert-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => { handleDeleteAlert(alertToDelete); setAlertToDelete(null); }}
              data-testid="delete-alert-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
